import { useMemo } from 'react'

import { useIntl } from 'react-intl'

import { ButtonGroup, FormattedMessage, useNavigation } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'
import { boolean, object, optional, refine } from '@subaio/validation'
import { Checkbox, Input } from '@subaio/webapp'

import { asContainer } from 'lib/container'
import { useSignupProof } from 'lib/useSignupProof'
import { useValidation } from 'lib/useValidation'

import { ButtonSolidSubmit } from 'components/Buttons'
import { CenterContent } from 'components/Layout'
import { useSnackbar } from 'components/Snackbars'

import { BottomContent } from './BottomContent'
import { FieldRow } from './FieldRow'
import { isPhoneNumber, normalizePhoneNumber } from './phoneNumber'
import { Title } from './Title'

export const container = asContainer<{}>()({
	path: '/sign-up',
	authentication: { type: 'public' },
	Running: ({ api }) => {
		const { onUpdateUrlTo } = useNavigation()
		const { handleSnackbars } = useSnackbar()
		const { fieldEmail, fieldString, passwordsMatchMessage, withLength } = useValidation()
		const getSignupProof = useSignupProof(api)

		const { locale } = useIntl()
		const languageSuffix = useMemo(() => {
			const [language] = locale.split('-')
			if (language === 'en') return ''
			return `-${language}`
		}, [locale])

		return (
			<CenterContent>
				<Form
					schema={object({
						ownerName: withLength(fieldString(), 1, 255),
						ownerEmail: withLength(fieldEmail(), 1, 320),
						phoneNumber: refine(
							fieldString(),
							'phoneNumber',
							(value) =>
								isPhoneNumber(value) ||
								'Enter a phone number with 8-15 digits, including the country code if provided.',
						),
						ownerPassword: withLength(fieldString(), 8, 128),
						ownerPasswordConfirm: refine(
							withLength(fieldString(), 8, 128),
							'passwordsMatch',
							(confirm, { branch }) => branch[0].ownerPassword === confirm || passwordsMatchMessage,
						),
						businessLegalName: withLength(fieldString(), 1, 32),
						acceptsMarketing: optional(boolean()),
						consent: refine(boolean(), 'consent', (value) => value === true || 'You must give consent to the solution'),
					})}
					onSubmit={handleSnackbars(
						async ({ ownerPasswordConfirm, consent, ...rest }) => {
							if (!consent) throw new Error('User did not consent')
							const proof = await getSignupProof()
							const { challengeId } = await api.post<{ challengeId: string }>('/auth/signup', {
								...rest,
								phoneNumber: normalizePhoneNumber(rest.phoneNumber),
								billingCountry: 'DK',
								challengeToken: proof.challengeToken,
								challengeSolution: proof.challengeSolution,
							})
							onUpdateUrlTo({ name: 'VerifyEmail', query: { 'challenge-id': challengeId } })
						},
						{
							401: 'Failed to sign up',
							default: 'Failed to sign up',
						},
					)}
					render={({
						ownerName,
						ownerEmail,
						phoneNumber,
						ownerPassword,
						ownerPasswordConfirm,
						businessLegalName,
						acceptsMarketing,
						consent,
					}) => (
						<>
							<Title>Sign up to Finance Data</Title>
							<FieldRow>
								<Input {...ownerName} label='Full name' autoComplete='full name' />
								<Input {...businessLegalName} label='Company name' autoComplete='businessLegalName' />
							</FieldRow>
							<FieldRow>
								<Input {...ownerEmail} label='Email' type='email' autoComplete='email' />
								<Input {...phoneNumber} label='Phone number' type='tel' autoComplete='tel' />
							</FieldRow>
							<FieldRow>
								<Input {...ownerPassword} label='Password' type='password' autoComplete='password' />
								<Input
									{...ownerPasswordConfirm}
									label='Password confirm'
									type='password'
									autoComplete='password confirm'
								/>
							</FieldRow>
							<Checkbox
								{...acceptsMarketing}
								label={
									<FormattedMessage
										id='signup.marketingConsent'
										defaultMessage='Yes, I would like Subaio to contact me by email or phone with news, updates and relevant information about Finance Data. I can withdraw my consent at any time.'
									/>
								}
							/>
							<Checkbox
								{...consent}
								label={
									<FormattedMessage
										id='signup.consent'
										defaultMessage='I accept the <termsOfUseLink>Terms of Use</termsOfUseLink> and acknowledge that I have read the <privacyPolicyLink>Privacy Policy</privacyPolicyLink>.'
										values={{
											termsOfUseLink: (node) => (
												<a
													target='_blank'
													href={`https://subaio.com/legal/finance-data/terms-of-use${languageSuffix}`}
													rel='noopener'
												>
													{node}
												</a>
											),
											privacyPolicyLink: (node) => (
												<a
													target='_blank'
													href={`https://subaio.com/legal/finance-data/privacy-policy/personal${languageSuffix}`}
													rel='noopener'
												>
													{node}
												</a>
											),
										}}
									/>
								}
							/>
							<BottomContent>
								<ButtonGroup center>
									<ButtonSolidSubmit label='Sign up' />
								</ButtonGroup>
							</BottomContent>
						</>
					)}
				/>
			</CenterContent>
		)
	},
})
